import { SortDirection } from '@mui/material';
import { useState } from 'react';
import { ConditionalStyles, Selector } from 'react-data-table-component';
import { rowExpandedStyle } from './table.styles';
import { ExpandableRowRecord, StoredTableConfig } from './table.types';

const defaultPerPageValue = 10;

const tableConfigVersion = 1;

const storageItemKey = (id: string) => `${id}_tableconfig_v${tableConfigVersion}`;

const getTableConfigById = (tableId: string): StoredTableConfig =>
  JSON.parse(localStorage.getItem(storageItemKey(tableId)) ?? '{}') || {};

export const useRowExpand = <T>(expandableRows: boolean | undefined, idField: keyof T) => {
  const [expandedRows, setExpandedRows] = useState<ExpandableRowRecord>({});

  const updateExpandedRows = (id: T[keyof T], expanded: boolean): void => {
    setExpandedRows(oldVal => {
      const newVal = { ...oldVal };
      newVal[id as string] = expanded;
      return newVal;
    });
  };

  const onRowExpandToggled = (): ((expanded: boolean, row: T) => void) => {
    return (expanded: boolean, row: T): void => {
      if (row[idField] !== undefined) {
        updateExpandedRows(row[idField], expanded);
      }
    };
  };

  const conditionalRowStyles = (): ConditionalStyles<T>[] | undefined => {
    return [
      {
        when: (row: T) => !!(row[idField] !== undefined && !!expandedRows[row[idField] as string]),
        style: rowExpandedStyle,
      },
    ];
  };

  return expandableRows
    ? {
        onRowExpandToggled: onRowExpandToggled(),
        conditionalRowStyles: conditionalRowStyles(),
      }
    : undefined;
};

export const getDefaultPerPage = (tableId?: string) => {
  if (tableId) {
    const config = getTableConfigById(tableId);
    return config.paginationPerPage || defaultPerPageValue;
  }
  return defaultPerPageValue;
};

export const savePerPage = (tableId: string, value: number) => {
  const currentConfig = getTableConfigById(tableId);
  const nextConfig: StoredTableConfig = { ...currentConfig, paginationPerPage: value };
  try {
    localStorage.setItem(storageItemKey(tableId), JSON.stringify(nextConfig));
  } catch (e) {
    console.warn(`Unable to save config for table ${tableId}`, e);
  }
};

export const defaultRowsPerPageOptions = [10, 25, 50, 100];

export const joinArrayKey = <T>(arr: T[], key: keyof T) => {
  return arr?.length ? arr.map(item => item[key]).join(', ') : '';
};

export const caseInsensitiveSort = <T>(rows: T[], selector: Selector<T>, direction: SortDirection) => {
  return rows.sort((rowA: T, rowB: T) => {
    try {
      // use the selector function to resolve your field names by passing the sort comparitors
      let aField = selector(rowA);
      let bField = selector(rowB);

      if (typeof aField === 'string') {
        aField = aField.toLowerCase();
      }

      if (typeof bField === 'string') {
        bField = bField.toLowerCase();
      }

      let comparison = 0;

      if (aField > bField) {
        comparison = 1;
      } else if (aField < bField) {
        comparison = -1;
      }

      return direction === 'desc' ? comparison * -1 : comparison;
    } catch (e) {
      return 0;
    }
  });
};

export const naturalSort = <T>(rows: T[], selector: Selector<T>, direction: SortDirection) => {
  return rows.sort((a, b) => {
    let aField = '';
    let bField = '';
    let ret = 0;

    try {
      aField = selector(a).toString();
    } catch (e) {
      ret = -1;
    }

    try {
      bField = selector(b).toString();
    } catch (e) {
      ret = 1;
    }

    ret = aField.localeCompare(bField, undefined, { numeric: true, sensitivity: 'base' });

    return direction === 'desc' ? ret * -1 : ret;
  });
};
